import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Display labels for attribute distribution headers.
const ATTR_LABELS = {
  co:         { short: 'CO',         full: 'Course Outcomes' },
  unit:       { short: 'Unit',       full: 'Units' },
  bl:         { short: 'BL',         full: "Bloom's Levels" },
  type:       { short: 'Type',       full: 'Question Types' },
  difficulty: { short: 'Level',      full: 'Difficulty Levels' },
  kp:         { short: 'KP',         full: 'Knowledge Priority' },
  po:         { short: 'PO',         full: 'Program Outcomes' },
};

/**
 * Course-level statistical summary:
 *   - Totals: total questions, active count, total marks
 *   - Per-enabled-attribute distribution cards with count / marks / % bar
 *
 * Mirrors the visual pattern used by TemplatePreviewModal so instructors get
 * the same "coverage at a glance" view whether they're authoring a paper or
 * auditing their question bank.
 */
function CourseStatsModal({ tenantId, course, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!tenantId || !course) return;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(
          `/question-bank/courses/${tenantId}/${course.CourseID}/stats`
        );
        if (res.data.success) setData(res.data.data);
      } catch {
        toast.error('Failed to load course statistics');
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId, course]);

  // Enrich distributions with percentages + sorted ordering (Unassigned last,
  // otherwise marks desc then value asc — matches TemplatePreviewModal).
  const prepared = useMemo(() => {
    if (!data) return {};
    const { totals, distributions, enabledAttributes } = data;
    const result = {};
    for (const code of enabledAttributes) {
      const rows = (distributions[code] || [])
        .map((r) => ({
          value: r.value,
          count: r.count,
          marks: Math.round(r.marks * 100) / 100,
          countPct: totals.total > 0 ? Math.round((r.count / totals.total) * 100) : 0,
          marksPct: totals.totalMarks > 0 ? Math.round((r.marks / totals.totalMarks) * 100) : 0,
          isUnassigned: r.value === '— Unassigned —',
        }))
        .sort((a, b) => {
          if (a.isUnassigned && !b.isUnassigned) return 1;
          if (!a.isUnassigned && b.isUnassigned) return -1;
          if (b.marks !== a.marks) return b.marks - a.marks;
          return String(a.value).localeCompare(String(b.value));
        });
      result[code] = rows;
    }
    return result;
  }, [data]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content qb-stats-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 style={{ margin: 0 }}>
              {course?.CourseCode} — {course?.CourseName}
            </h2>
            <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 2 }}>
              Question Bank Statistical Summary
            </div>
          </div>
          <button className="btn btn-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
            Loading statistics…
          </div>
        ) : !data ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#c62828' }}>
            No statistics available.
          </div>
        ) : (
          <div className="modal-body">
            {/* Top totals */}
            <div className="qb-stats-totals">
              <div className="qb-stats-total-card">
                <div className="qb-stats-total-label">Total Questions</div>
                <div className="qb-stats-total-value">{data.totals.total}</div>
              </div>
              <div className="qb-stats-total-card">
                <div className="qb-stats-total-label">Active</div>
                <div className="qb-stats-total-value">
                  {data.totals.active}
                  <span className="qb-stats-total-sub">
                    {' '}/ {data.totals.total}
                  </span>
                </div>
              </div>
              <div className="qb-stats-total-card">
                <div className="qb-stats-total-label">Total Marks</div>
                <div className="qb-stats-total-value">
                  {Math.round(data.totals.totalMarks * 100) / 100}
                </div>
              </div>
            </div>

            {/* Per-attribute distributions */}
            {data.enabledAttributes.length === 0 ? (
              <div className="qb-stats-empty-msg">
                No attributes are enabled for this tenant. Enable attributes
                under <strong>QP Settings → Question Bank Settings</strong>
                {' '}to see distribution analysis.
              </div>
            ) : (
              <>
                <div className="qp-preview-section-title">
                  Attribute Distribution
                  <span className="qp-preview-section-hint">
                    Based on {data.totals.total}{' '}
                    {data.totals.total === 1 ? 'question' : 'questions'} totalling{' '}
                    {Math.round(data.totals.totalMarks * 100) / 100} marks
                  </span>
                </div>

                <div className="qp-preview-stats-grid">
                  {data.enabledAttributes.map((code) => {
                    const rows = prepared[code] || [];
                    const lbl = ATTR_LABELS[code] || { short: code, full: code };
                    if (rows.length === 0) {
                      return (
                        <div className="qp-preview-stats-card" key={code}>
                          <div className="qp-preview-stats-card-title">{lbl.full}</div>
                          <div className="qp-preview-stats-empty">No data</div>
                        </div>
                      );
                    }
                    return (
                      <div className="qp-preview-stats-card" key={code}>
                        <div className="qp-preview-stats-card-title">{lbl.full}</div>
                        <table className="qp-preview-stats-table">
                          <thead>
                            <tr>
                              <th>{lbl.short}</th>
                              <th style={{ textAlign: 'right' }}>Qs</th>
                              <th style={{ textAlign: 'right' }}>Marks</th>
                              <th>% (Marks)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r) => (
                              <tr
                                key={r.value}
                                className={r.isUnassigned ? 'qp-preview-stats-unassigned' : ''}
                              >
                                <td>{r.value}</td>
                                <td style={{ textAlign: 'right' }}>{r.count}</td>
                                <td style={{ textAlign: 'right' }}>{r.marks}</td>
                                <td>
                                  <div className="qp-preview-stats-barwrap">
                                    <div
                                      className="qp-preview-stats-bar"
                                      style={{ width: `${Math.min(r.marksPct, 100)}%` }}
                                    />
                                    <span className="qp-preview-stats-pct">
                                      {r.marksPct}%
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default CourseStatsModal;
